import { Common } from '@stockbit/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectors as userSelector } from 'lib/entities/user';
import {
  effects as companyEffects,
  selectors as companySelector,
} from 'lib/entities/company';
import { Mentions } from 'antd';
import debounce from 'lodash.debounce';
import { MentionSuggestionResult, TextareaMentions } from './styled';

const { Flex, Text } = Common;
const { Option: MentionOption } = Mentions;

interface TextareaProps {
  value?: string;
  onChange?: (text: string) => void;
  [key: string]: any;
}

function Textarea({ value, onChange, ...props }: TextareaProps) {
  const textareaRef = useRef<any>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [mentionPrefix, setMentionPrefix] = useState('@');

  const dispatch = useDispatch();
  const {
    search: { data: userSearch },
  } = useSelector(userSelector);
  const {
    search: { data: companySearch },
  } = useSelector(companySelector);

  const SEARCH_DATA = {
    $: {
      data: companySearch,
      dispatch: companyEffects.getSearchCompany,
    },
  };

  const debouncedSearch = useCallback(
    debounce((keyword, prefix) => {
      const action = SEARCH_DATA[prefix].dispatch;
      dispatch(action(keyword));
    }, 500),
    [],
  );

  const handleSearch = (keyword, prefix) => {
    setSearchKeyword(keyword);
    setMentionPrefix(prefix);

    debouncedSearch(keyword, prefix);
  };

  const searchResult = useMemo(() => {
    if (searchKeyword.length >= 2) {
      return SEARCH_DATA[mentionPrefix]?.data;
    }
    return [];
  }, [userSearch, companySearch, searchKeyword]);

  useEffect(() => {
    // Place active cursor on last chart
    if (textareaRef.current) {
      const { textarea } = textareaRef.current;
      textarea.focus();
      textarea.selectionStart = value.length; // Move cursor to end
      textarea.selectionEnd = textarea.selectionStart;
    }
  }, []);

  return (
    <>
      <MentionSuggestionResult id="compose-notes-container" />
      <TextareaMentions
        {...props}
        ref={textareaRef}
        prefix={['$']}
        value={value}
        onChange={onChange}
        onSearch={handleSearch}
        notFoundContent={null}
        getPopupContainer={() => document.getElementById('compose-notes-container')}
      >
        {searchResult.map(({ id, name, ...data }, index) => (
          <MentionOption
            // eslint-disable-next-line react/no-array-index-key
            key={`${id}-${index}`}
            value={name}
            className="compose-notes-option"
          >
            <Flex align="center">
              <Text mr="8px">
                {mentionPrefix}
                {name}
              </Text>
              <Text>{data.desc}</Text>
            </Flex>
          </MentionOption>
        ))}
      </TextareaMentions>
    </>
  );
}

export default Textarea;
